import React, { FC } from 'react';

import { useTheme } from 'styled-components';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import Headshot, { HEADSHOT_TYPES } from '@nhl/nhl-react-ui/lib/Headshot';

import { MODERN_HEADSHOT_ERA } from '~/core/constants';
import { PlayerPosition, LeagueNames } from '~/core/enums';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { Player } from '../../types';
import { PlayerFeaturedStats } from '../playerFeaturedStats/PlayerFeaturedStats';
import { PlayerInfoAndSocials, PlayerInfoComponent } from '../playerInfoAndSocials/PlayerInfoAndSocials';

import { LargePlayerNameSelect, SmallPlayerNameSelect } from './PlayerNameSelect';

import {
    BackgroundBlurContainer,
    HeadshotBirthplaceContainer,
    LargeBadgeContainer,
    MediumWidthContainerLeftDiv,
    MediumWidthContainerRightDiv,
    PlayerBadge,
    PlayerHeaderImage,
    PlayerInfoMediumWidthContainer,
    SmallBackgroundContainer,
    SmallBadgeContainer,
} from './styles';

type PlayerHeaderComponentProps = {
    player: Player;
};

export const PlayerHeaderComponent: FC<PlayerHeaderComponentProps> = ({ player }) => {
    const theme = useTheme();
    const screenSize = useSizeWithThrottle();

    const isSmall = screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const isMedium = screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.large;
    const isLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    const isGoalie = player.position === PlayerPosition.goalie;

    const lastPlayedSeason = player?.seasonTotals
        ?.filter((season) => season.leagueAbbrev === LeagueNames.nhl)
        ?.at(-1)?.season;
    const isModernHeadshotEra = (lastPlayedSeason as number) >= MODERN_HEADSHOT_ERA;

    const headshot = (
        <Headshot
            forceSimpleCircle={!isModernHeadshotEra}
            urlOverride={player.headshot}
            type={isGoalie ? HEADSHOT_TYPES.GOALIE : HEADSHOT_TYPES.SKATER}
            size={isModernHeadshotEra || isLarge ? 160 : 128}
            backgroundColor={
                player.currentTeamAbbrev ? theme.Team[player.currentTeamAbbrev]?.gamecenter : theme.Surface.secondary
            }
        />
    );

    const featuredStats =
        player.featuredStats === undefined ? null : (
            <PlayerFeaturedStats
                featuredStats={player.featuredStats}
                isGoalie={isGoalie}
                isPlayerInactive={!player.isActive}
            />
        );

    return isSmall ? (
        <>
            <PlayerHeaderImage $imageUrl={player?.heroImage} />
            <SmallBackgroundContainer>
                {headshot}

                <SmallPlayerNameSelect player={player} isModern={isModernHeadshotEra} />

                <SmallBadgeContainer>
                    {player.badges?.map((badge, index) => (
                        <PlayerBadge
                            $isSmall
                            key={index}
                            alt={parseValueFromLocalizedAttribute(badge.title)}
                            src={parseValueFromLocalizedAttribute(badge.logoUrl)}
                        />
                    ))}
                </SmallBadgeContainer>
            </SmallBackgroundContainer>

            {featuredStats}

            <PlayerInfoAndSocials player={player} />
        </>
    ) : (
        <>
            {isMedium ? null : <LargePlayerNameSelect player={player} />}

            <PlayerHeaderImage $imageUrl={player?.heroImage}>
                <BackgroundBlurContainer>
                    {isMedium ? (
                        <PlayerInfoMediumWidthContainer>
                            <MediumWidthContainerLeftDiv>
                                {headshot}
                                {/*
                                Waiting to hear back if we're going to keep this or remove it
                                <PlayerSocialsComponent player={player} />
                                */}
                            </MediumWidthContainerLeftDiv>

                            <MediumWidthContainerRightDiv>
                                <SmallPlayerNameSelect player={player} />
                                <PlayerInfoComponent player={player} />
                            </MediumWidthContainerRightDiv>
                        </PlayerInfoMediumWidthContainer>
                    ) : (
                        <>
                            <HeadshotBirthplaceContainer>
                                {headshot}
                                <PlayerInfoAndSocials player={player} />
                            </HeadshotBirthplaceContainer>
                            {featuredStats}
                        </>
                    )}
                </BackgroundBlurContainer>
            </PlayerHeaderImage>

            {player.badges && player.badges.length > 0 && (
                <LargeBadgeContainer>
                    {player.badges.map((badge, index) => (
                        <PlayerBadge
                            key={index}
                            alt={parseValueFromLocalizedAttribute(badge.title)}
                            src={parseValueFromLocalizedAttribute(badge.logoUrl)}
                        />
                    ))}
                </LargeBadgeContainer>
            )}

            {isMedium && featuredStats}
        </>
    );
};
